import { useState, useEffect, useCallback } from 'react';
import {
  ProductLookupItem,
  CompositeLookupItem, // Import CompositeLookupItem
} from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.ts';

interface LookupItem {
  id: number;
  description: string;
  type: 'product' | 'composite'; // To distinguish between product and composite
}

interface UseProductAndCompositeLookupResult {
  lookupItems: LookupItem[];
  loading: boolean;
  error: string | null;
  fetchLookupItems: () => Promise<void>;
}

export const useProductAndCompositeLookup = (): UseProductAndCompositeLookupResult => {
  const [lookupItems, setLookupItems] = useState<LookupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLookupItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [productsResponse, compositesResponse] = await Promise.all([
        apiClient<{ data: ProductLookupItem[] }>('/products/lookup', { allPages: true }),
        apiClient<{ data: CompositeLookupItem[] }>('/composites/lookup', { allPages: true }), // Fetch composites lookup
      ]);

      const products: LookupItem[] = productsResponse.data.map((item) => ({
        id: item.id,
        description: `Product: ${item.description}`,
        type: 'product',
      }));

      const composites: LookupItem[] = compositesResponse.data.map((item) => ({
        id: item.id,
        description: `Composite: ${item.description}`,
        type: 'composite',
      }));

      // Combine and sort by description
      const combinedItems = [...products, ...composites].sort((a, b) =>
        a.description.localeCompare(b.description)
      );

      setLookupItems(combinedItems);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch products/composites: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching products/composites.');
      }
      setLookupItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLookupItems();
  }, [fetchLookupItems]);

  return {
    lookupItems,
    loading,
    error,
    fetchLookupItems,
  };
};